/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { BlockType } from "@vienna/common";

import { Block } from "src/classes/block";
import {
  BlockWindow,
  CodeBlock,
  ColorBlock,
  FileBlock,
  ImageBlock,
  LinkBlock,
  PlateWindowBlock,
  TextBlock,
} from "src/classes/block/block-types";
import { Plate } from "src/classes/plate/Plate";
import { CodeBlock as CodeBlockContent } from "src/components/Block/BlockTypes/CodeBlock";
import { ColorBlockContent } from "src/components/Block/BlockTypes/ColorBlock";
import { EmbeddedPlate } from "src/components/Block/BlockTypes/EmbeddedPlate";
import { FileBlockContent } from "src/components/Block/BlockTypes/FileBlock";
import { ImageBlockContent } from "src/components/Block/BlockTypes/ImageBlock";
import { LinkBlockContent } from "src/components/Block/BlockTypes/LinkBlock";
import { TextBlockContent } from "src/components/Block/BlockTypes/TextBlock";
import { ErrorBlock } from "src/components/Block/Placeholders/ErrorBlock";

interface Props {
  block: Block;
  editable: boolean;
  plate: Plate;
}

export function BlockContent({ block, plate, editable }: Props) {
  const blockType = block.observe((state) => state.type);
  return (
    <>
      {(() => {
        switch (blockType) {
          case BlockType.Text:
            return <TextBlockContent block={block as TextBlock} editable={editable} plate={plate} />;
          case BlockType.Image:
            return <ImageBlockContent block={block as ImageBlock} editable={editable} plate={plate} />;
          case BlockType.Link:
            return <LinkBlockContent block={block as LinkBlock} plate={plate} />;
          case BlockType.Color:
            return <ColorBlockContent block={block as ColorBlock} editable={editable} plate={plate} />;
          case BlockType.Code:
            return <CodeBlockContent block={block as CodeBlock} editable={editable} />;
          case BlockType.File:
            return <FileBlockContent block={block as FileBlock} plate={plate} />;
          case BlockType.PlateWindow:
            return <EmbeddedPlate block={block as PlateWindowBlock} />;
          case BlockType.BlockWindow:
            const blockWindow = block as BlockWindow;
            return blockWindow.embeddedBlock ? (
              <BlockContent block={blockWindow.embeddedBlock} plate={plate} editable={false} />
            ) : (
              <ErrorBlock>Connected block not found.</ErrorBlock>
            );
          default:
            return <ErrorBlock>Block type not found.</ErrorBlock>;
        }
      })()}
    </>
  );
}
